import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import split from 'lodash/split';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassPlus, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

import { ageRatingBoard } from 'Helpers/Geo/data';
import useViewport from 'Hooks/useViewport';
import AgeGatingContainer from 'Components/Product/AgeGatingContainer';
import ProductCover from 'Components/Utility/ProductCover';
import FluidImg from 'Components/Utility/FluidImg';
import pauseYTVideos from 'Helpers/Video/VideoHelpers';
import SliderVideo from 'Components/Product/ProductSlider/SliderVideo';
import LazyModal from 'Components/Utility/LazyModal';
import TrustPilotBanner from 'Components/Product/Carousel/TrustPilotBanner';

import 'Components/Product/Carousel/ProductMediaCarousel.scss';

const ProductMediaCarousel = ({
  cover,
  coverAltText,
  name,
  displayType,
  coverType,
  sizesOverride,
  screenshots,
  videos,
  bundleImages,
  bundleVideos,
  passedAgeGate,
  ageRatings,
  unlinkCoverFromVideo,
  dateOfBirth,
  carouselType,
}) => {
  const [maxModalOpen, setMaxModalOpen] = useState(false);
  const [screenshotSlide, setScreenshotSlide] = useState(1);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [trackPosition, setTrackPosition] = useState(0);
  const [activeGameNumber, setActiveGameNumber] = useState(0);
  const [interacted, setInteracted] = useState(false);

  const {
    currentBreakpoint,
  } = useViewport();

  const slideWidths = {
    lg: 620,
    xl: 740,
    xxl: 870,
  };

  let trackSlidesToShow = 4;
  if (carouselType === 'non game') {
    trackSlidesToShow = 5;
  }
  const trackGap = 12;
  const trackSlideWidth = (slideWidths[currentBreakpoint] - (trackGap * (trackSlidesToShow - 1))) / trackSlidesToShow; // eslint-disable-line max-len

  const sliderRef = useRef(null);
  const sliderTrackRef = useRef(null);

  const noMedia = isEmpty(screenshots) && isEmpty(videos) && isEmpty(bundleImages) && isEmpty(bundleVideos); // eslint-disable-line max-len

  if (noMedia) {
    return (
      <div className={classNames('ProductMediaCarousel ProductMediaCarousel--singleCover')}>
        <ProductCover
          cover={cover}
          alt={coverAltText || `Product cover for ${name}`}
          sizesOverride={sizesOverride}
          displayType={displayType}
          coverType={coverType}
        />
        <TrustPilotBanner />
      </div>
    );
  }

  const ageGateThresholds = {
    ESRB: 17,
    USK: 16,
    PEGI: 18,
    ACB: 18,
  };

  let ageGateRequired = false;
  if (!passedAgeGate && ageRatings[ageRatingBoard] >= ageGateThresholds[ageRatingBoard]) {
    ageGateRequired = true;
  }

  if (dateOfBirth && dateOfBirth !== '') {
    const dobDate = new Date(dateOfBirth);
    const year = dobDate.getFullYear();
    const month = dobDate.getMonth() + 1;
    const day = dobDate.getDate();
    if (!year || !month || !day) return false;

    const today = Date.now();
    const userDate = new Date(year, month, day);
    const ageDifference = (today - userDate) / 1000 / 60 / 60 / 24 / 363.5;

    if (ageDifference >= (ageRatings[ageRatingBoard])) {
      ageGateRequired = false;
    }
  }

  const sliderVideos = videos.map(video => (
    { video: true, src: video.src, cover: video.cover }
  ));
  const sliderBundleVideos = bundleVideos.map(video => (
    { video: true, src: video.src, cover: video.cover }
  ));

  let useCoverForFirstVideo = true;
  if (isEmpty(sliderVideos) || ageGateRequired || unlinkCoverFromVideo) {
    useCoverForFirstVideo = false;
  }

  const slides = [];
  if (cover && !useCoverForFirstVideo) {
    let alt = split(cover, '.')[0];
    if (coverAltText) alt = coverAltText;
    slides.push({
      image: cover,
      alt,
      type: 'cover',
      number: slides.length + 1,
      order: '0',
      cover: true,
    });
  }
  if (!isEmpty(sliderVideos) && !ageGateRequired) {
    sliderVideos.forEach((video, i) => {
      let alt = split(video.cover, '.')[0];
      let useYouTubeCover = true;
      if (useCoverForFirstVideo && i === 0) {
        useYouTubeCover = false;
        alt = `Product cover for ${name}`;
        if (coverAltText) alt = coverAltText;
      }

      slides.push({
        image: video.cover,
        alt,
        type: 'video',
        number: slides.length + 1,
        order: '0',
        video: video.src,
        ytCover: useYouTubeCover,
      });
    });
  }
  if (!isEmpty(sliderBundleVideos)) {
    sliderBundleVideos.forEach((video) => {
      const alt = split(video.cover, '.')[0];
      slides.push({
        image: video.cover,
        alt,
        type: 'bundle-video',
        number: slides.length + 1,
        order: '0',
        video: video.src,
      });
    });
  }
  if (!isEmpty(bundleImages)) {
    bundleImages.forEach((image, i) => {
      slides.push({
        image: image.slug,
        alt: image.alt,
        type: 'image',
        number: slides.length + 1,
        screenshotNumber: i + 1,
        order: image.order || '99',
      });
    });
  }
  if (!isEmpty(screenshots)) {
    screenshots.forEach((image, i) => {
      slides.push({
        image: image.slug,
        alt: image.alt,
        type: 'image',
        number: slides.length + 1,
        screenshotNumber: i + 1,
        order: image.order || '99',
      });
    });
  }

  const viewScreenshotModal = (screenshotNumber) => {
    setMaxModalOpen(true);
    setScreenshotSlide(screenshotNumber);
  };

  const changeScreenshot = (screenshotNumber, direction) => {
    if (direction === 'previous') {
      setScreenshotSlide(screenshotNumber - 1);
    }
    if (direction === 'next') {
      setScreenshotSlide(screenshotNumber + 1);
    }
  };

  const totalScreenshots = slides.filter(slide => slide.screenshotNumber);
  const currentScreenshot = slides.find(slide => slide.screenshotNumber === screenshotSlide);
  const nextScreenshot = slides.find(slide => slide.screenshotNumber === screenshotSlide + 1);

  const clickTrackItem = (number) => {
    if (slides.length > trackSlidesToShow) {
      setTrackPosition(-(number) * (trackSlideWidth + trackGap));
    }
    setSliderPosition(-(slideWidths[currentBreakpoint] * number));
    setActiveGameNumber(number);

    if (!interacted) {
      setInteracted(true);
    }

    pauseYTVideos();
  };

  const loadTrackNext = () => {
    const totalMovement = (trackSlideWidth * trackSlidesToShow) + (trackGap * trackSlidesToShow);
    // trackLength is negative because the track moves to the left from a start of zero.
    const trackLength = -(trackSlideWidth + trackGap) * (slides.length - 1);

    if ((trackPosition - totalMovement) < trackLength) {
      setTrackPosition(trackLength);
    } else {
      setTrackPosition(trackPosition - totalMovement);
    }

    if (!interacted) {
      setInteracted(true);
    }
  };

  const loadTrackPrevious = () => {
    const totalMovement = (trackSlideWidth * trackSlidesToShow) + (trackGap * trackSlidesToShow);

    if ((trackPosition + totalMovement) > 0) {
      setTrackPosition(0);
    } else {
      setTrackPosition(trackPosition + totalMovement);
    }

    if (!interacted) {
      setInteracted(true);
    }
  };

  const loadNext = () => {
    if (activeGameNumber + 1 === slides.length) {
      setSliderPosition(0);
      setActiveGameNumber(0);
    } else {
      setSliderPosition(sliderPosition - slideWidths[currentBreakpoint]);
      setActiveGameNumber(activeGameNumber + 1);
    }

    // Move track
    if (slides.length > trackSlidesToShow) {
      if (activeGameNumber + 1 === slides.length) {
        setTrackPosition(0);
      } else {
        setTrackPosition(-(activeGameNumber + 1) * (trackSlideWidth + trackGap));
      }
    }

    if (!interacted) {
      setInteracted(true);
    }

    pauseYTVideos();
  };

  const loadPrevious = () => {
    if (activeGameNumber === 0) {
      setSliderPosition(sliderPosition - (slideWidths[currentBreakpoint] * (slides.length - 1)));
      setActiveGameNumber(slides.length - 1);
    } else {
      setSliderPosition(sliderPosition + slideWidths[currentBreakpoint]);
      setActiveGameNumber(activeGameNumber - 1);
    }

    // Move track
    if (slides.length > trackSlidesToShow) {
      if (activeGameNumber === 0) {
        setTrackPosition(-(slides.length - 1) * (trackSlideWidth + trackGap));
      } else {
        setTrackPosition(-(activeGameNumber - 1) * (trackSlideWidth + trackGap));
      }
    }

    if (!interacted) {
      setInteracted(true);
    }

    pauseYTVideos();
  };

  return (
    <React.Fragment>
      <div className={classNames('ProductMediaCarousel ProductMediaCarousel--includesTrustPilot', { 'ProductMediaCarousel--singleCover': slides.length === 1 })}>
        <div className="ProductMediaCarousel__main">
          <ul className="ProductMediaCarousel__main__slides" ref={sliderRef} style={{ left: `${sliderPosition}px` }}>
            {slides.map((slide, i) => {
              if (slide.type === 'cover') {
                return (
                  <li
                    key={slide.number}
                    className="ProductMediaCarousel__main__slides__slide"
                    style={{ width: `${slideWidths[currentBreakpoint]}px` }}
                    aria-hidden={i !== activeGameNumber}
                  >
                    {(i === 0 || interacted) &&
                      <ProductCover
                        cover={slide.image}
                        alt={coverAltText || `Product cover for ${name}`}
                        sizesOverride={sizesOverride}
                        displayType={displayType}
                        coverType={coverType}
                      />
                    }
                  </li>
                );
              }

              if (slide.type === 'video' || slide.type === 'bundle-video') {
                return (
                  <li
                    key={slide.number}
                    className="ProductMediaCarousel__main__slides__slide"
                    style={{ width: `${slideWidths[currentBreakpoint]}px` }}
                    aria-hidden={i !== activeGameNumber}
                  >
                    {(i === 0 || interacted) &&
                      <SliderVideo
                        cover={slide.image}
                        alt={slide.alt}
                        src={slide.video}
                        ageGateRequired={ageGateRequired}
                        ytCover={slide.ytCover}
                        sizesOverride={sizesOverride}
                        displayType={displayType}
                        coverType={coverType}
                      />
                    }
                  </li>
                );
              }

              return (
                <li
                  key={slide.number}
                  className={classNames('ProductMediaCarousel__main__slides__slide', { 'ProductMediaCarousel__main__slides__slide--ageGated': ageGateRequired })}
                  style={{ width: `${slideWidths[currentBreakpoint]}px` }}
                  aria-hidden={i !== activeGameNumber}
                >
                  <div className="responsive-image-island">
                    <div className="responsive-image responsive-image--16by9">
                      {(i === 0 || interacted) &&
                        <FluidImg
                          ratio16by9={carouselType !== 'non game'}
                          className={classNames('img-force-full slide-image', { 'cover-image-foreground': carouselType === 'non game' })}
                          src={slide.image}
                          alt={slide.alt}
                          sizesOverride={sizesOverride}
                          lazy={i > 0}
                        />
                      }
                      {(i === 0 || interacted) && carouselType === 'non game' &&
                        <FluidImg
                          className="cover-image-background"
                          src={slide.image}
                          alt="Blurred background image"
                          sizesOverride={sizesOverride}
                          lazy={i > 0}
                        />
                      }
                    </div>
                  </div>

                  {ageGateRequired &&
                    <AgeGatingContainer ratings={ageRatings} lazyLoad={false} className="ProductMediaCarousel__main__slide__ageGating" />
                  }

                  {(i === 0 || interacted) && slide.type === 'image' && slide.screenshotNumber && !ageGateRequired &&
                    <button className="ProductMediaCarousel__main__maximiseButton" onClick={() => { viewScreenshotModal(slide.screenshotNumber); }}>
                      <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                    </button>
                  }
                </li>
              );
            })}
          </ul>

          {slides.length > 1 &&
            <>
              <div className="ProductMediaCarousel__main__leftArrow">
                <button
                  className="ProductMediaCarousel__main__leftArrow__button"
                  onClick={() => loadPrevious()}
                >
                  <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
                </button>
              </div>
              <div className="ProductMediaCarousel__main__rightArrow">
                <button
                  className="ProductMediaCarousel__main__rightArrow__button"
                  onClick={() => loadNext()}
                >
                  <FontAwesomeIcon icon={faChevronRight} title="Next" />
                </button>
              </div>
            </>
          }
        </div>

        <TrustPilotBanner />

        {slides.length > 1 &&
          <div
            ref={sliderTrackRef}
            className={classNames('ProductMediaCarousel__track', { 'ProductMediaCarousel__track--ageGated': ageGateRequired })}
          >
            <div className="ProductMediaCarousel__track__slides" style={{ left: `${trackPosition}px` }}>
              {slides.map((slide, i) => {
                // Track full length is negative because the track moves to the left from a start of zero.
                const visibleTrack = trackPosition - ((trackSlideWidth + trackGap) * trackSlidesToShow); // eslint-disable-line max-len
                const slidePosition = 0 - (i * (trackSlideWidth + trackGap));
                const isSlideVisible = (slidePosition > visibleTrack) && (slidePosition <= trackPosition); // eslint-disable-line max-len

                if (i > (trackSlidesToShow - 1) && !interacted) return false;

                if (slide.type === 'cover' || slide.type === 'bundle-video') {
                  return (
                    <button
                      className={classNames('ProductMediaCarousel__track__slides__slide', { 'ProductMediaCarousel__track__slides__slide--active': activeGameNumber === i })}
                      style={{ width: `${trackSlideWidth}px` }}
                      aria-hidden={!isSlideVisible}
                      tabIndex={0}
                      key={slide.number}
                      onClick={() => { clickTrackItem(i); }}
                    >
                      {(i <= trackSlidesToShow || interacted) &&
                        <ProductCover
                          cover={slide.image}
                          alt={slide.alt}
                          sizesOverride="(min-width: 1520px) 208px, (min-width: 1200px) 176px, (min-width: 992px) 146px, 98vw"
                          displayType={displayType}
                          coverType={carouselType === 'non game' ? 'square' : coverType}
                          key={slide.number}
                          adaptiveSize={carouselType === 'non game'}
                        />
                      }
                    </button>
                  );
                }

                return (
                  <button
                    key={slide.number}
                    aria-label="View image"
                    className={classNames('ProductMediaCarousel__track__slides__slide', { 'ProductMediaCarousel__track__slides__slide--active': activeGameNumber === i })}
                    style={{ width: `${trackSlideWidth}px` }}
                    aria-hidden={!isSlideVisible}
                    tabIndex={0}
                    onClick={() => { clickTrackItem(i); }}
                  >
                    <div className="responsive-image-island">
                      <div className={classNames('responsive-image', { 'responsive-image--16by9': carouselType !== 'non game' }, { 'responsive-image--1by1': carouselType === 'non game' })}>
                        {(i <= trackSlidesToShow || interacted) &&
                          <FluidImg
                            ratio16by9={carouselType !== 'non game'}
                            className={classNames('img-force-full slide-image', { 'cover-image-foreground': carouselType === 'non game' })}
                            youtubeId={slide.ytCover ? slide.video : null}
                            src={slide.image}
                            alt={slide.alt}
                            sizesOverride="(min-width: 1520px) 208px, (min-width: 1200px) 176px, (min-width: 992px) 146px, 98vw"
                          />
                        }
                        {(i <= trackSlidesToShow || interacted) && carouselType === 'non game' &&
                          <FluidImg
                            className="cover-image-background"
                            youtubeId={slide.ytCover ? slide.video : null}
                            src={slide.image}
                            alt="Blurred background image"
                            sizesOverride="(min-width: 1520px) 208px, (min-width: 1200px) 176px, (min-width: 992px) 146px, 98vw"
                          />
                        }
                      </div>
                    </div>

                    {slide.type === 'video' &&
                      <div className="ProductMediaCarousel__track__slide__buttonContainer">
                        <img src="https://cdn.fanatical.com/production/icons/youtube-icon-white.svg" alt="YouTube" loading="lazy" className="ProductMediaCarousel__track__slide__buttonContainer__img" />
                      </div>
                    }
                  </button>
                );
              })}
            </div>

            {slides.length > trackSlidesToShow &&
              <>
                <button
                  className="ProductMediaCarousel__track__leftArrow"
                  disabled={trackPosition === 0}
                  onClick={() => loadTrackPrevious()}
                >
                  <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
                </button>
                <button
                  className="ProductMediaCarousel__track__rightArrow"
                  disabled={trackPosition === -(trackSlideWidth + trackGap) * (slides.length - 1)}
                  onClick={() => loadTrackNext()}
                >
                  <FontAwesomeIcon icon={faChevronRight} title="Next" />
                </button>
              </>
            }
          </div>
        }
      </div>

      {!isEmpty(totalScreenshots) &&
        <LazyModal
          open={maxModalOpen}
          setOpen={setMaxModalOpen}
          className="ProductMediaModal"
        >
          <>
            <div className="responsive-image-island">
              <div className="responsive-image responsive-image--16by9">
                <FluidImg
                  ratio16by9={carouselType !== 'non game'}
                  className="img-force-full top-image"
                  src={currentScreenshot.image}
                  alt={currentScreenshot.alt}
                  sizesOverride="(min-width: 1520px) 1340px, (min-width: 1200px) 1140px, (min-width: 992px) 960px, 90vw"
                />
                {nextScreenshot &&
                  <FluidImg
                    ratio16by9={carouselType !== 'non game'}
                    className="img-force-full bottom-image"
                    src={nextScreenshot.image}
                    alt={nextScreenshot.alt}
                    sizesOverride="(min-width: 1520px) 1340px, (min-width: 1200px) 1140px, (min-width: 992px) 960px, 90vw"
                  />
                }
              </div>
            </div>

            <div className="ProductMediaModal__controls">
              <div className="ProductMediaModal__controls__buttonContainer ProductMediaModal__controls__buttonContainer--previous">
                {currentScreenshot.screenshotNumber !== 1 &&
                  <button
                    className="ProductMediaModal__controls__buttonContainer__button"
                    onClick={() => { changeScreenshot(currentScreenshot.screenshotNumber, 'previous'); }}
                  >
                    <FormattedMessage
                      id="product-media-carousel.controls.button.previous"
                      defaultMessage="Previous"
                    />
                  </button>
                }
              </div>
              <div className="ProductMediaModal__controls__total">
                <FormattedMessage
                  id="product-media-carousel.controls.total"
                  defaultMessage="{current} of {total} screenshots"
                  values={{
                    current: currentScreenshot.screenshotNumber,
                    total: totalScreenshots.length,
                  }}
                />
              </div>
              <div className="ProductMediaModal__controls__buttonContainer ProductMediaModal__controls__buttonContainer--next">
                {currentScreenshot.screenshotNumber !== totalScreenshots.length &&
                  <button
                    className="ProductMediaModal__controls__buttonContainer__button"
                    onClick={() => { changeScreenshot(currentScreenshot.screenshotNumber, 'next'); }}
                  >
                    <FormattedMessage
                      id="product-media-carousel.controls.button.next"
                      defaultMessage="Next"
                    />
                  </button>
                }
              </div>
            </div>
          </>
        </LazyModal>
      }
    </React.Fragment>
  );
};

ProductMediaCarousel.propTypes = {
  cover: PropTypes.string,
  coverAltText: PropTypes.string,
  coverType: PropTypes.string,
  displayType: PropTypes.string,
  name: PropTypes.string,
  sizesOverride: PropTypes.string,
  screenshots: PropTypes.arrayOf(PropTypes.object),
  videos: PropTypes.arrayOf(PropTypes.object),
  bundleImages: PropTypes.arrayOf(PropTypes.object),
  bundleVideos: PropTypes.arrayOf(PropTypes.object),
  ageRatings: PropTypes.object,
  passedAgeGate: PropTypes.bool.isRequired,
  unlinkCoverFromVideo: PropTypes.bool,
  dateOfBirth: PropTypes.string,
  carouselType: PropTypes.string,
};

ProductMediaCarousel.defaultProps = {
  cover: '',
  coverAltText: '',
  coverType: '',
  displayType: '',
  name: '',
  sizesOverride: '',
  screenshots: [],
  videos: [],
  bundleImages: [],
  bundleVideos: [],
  ageRatings: {},
  unlinkCoverFromVideo: false,
  dateOfBirth: '',
  carouselType: '',
};

const mapStateToProps = ({
  ageGating: { success },
  auth: { dob },
}) => ({
  passedAgeGate: success,
  dateOfBirth: dob,
});

export default connect(mapStateToProps)(ProductMediaCarousel);
